import type { FC, ReactNode } from 'react';

import { DateTimeRenderer } from '@/components/UI/DateRenderers/DateRenderers';
import { _isNil } from '@/littledash';
import type { ISODateTime } from '@/model/Common.model';

import styles from './InformationCard.module.scss';
import TextSkeleton from './TextSkeleton';

export interface InformationCardData {
  author: string | null;
  owner: string | null;
  createdAt: ISODateTime | null;
  type: string | null;
  description: string | null;
  plannedAnimals: number | null;
  livingAnimals: number | null;
  deceasedAnimals: number | null;
  groups: number | null;
}

interface InformationCardProps {
  information: InformationCardData;
  loading?: boolean;
}

interface InformationFieldProps {
  label: string;
  value: ReactNode;
  loading?: boolean;
  skeletonWidth?: string;
  skeletonLines?: number;
}

const fieldContent = ({
  value,
  loading,
  skeletonWidth,
  skeletonLines,
}: Omit<InformationFieldProps, 'label'>): ReactNode => {
  if (loading === true) {
    return <TextSkeleton width={skeletonWidth} lines={skeletonLines} />;
  }

  return _isNil(value) ? '—' : value;
};

const InformationField: FC<InformationFieldProps> = ({
  label,
  value,
  loading = false,
  skeletonWidth,
  skeletonLines,
}) => (
  <div className={styles.Field}>
    <dt className="basier-med">{label}:</dt>
    <dd aria-busy={loading}>{fieldContent({ value, loading, skeletonWidth, skeletonLines })}</dd>
  </div>
);

const InformationCard: FC<InformationCardProps> = ({ information, loading = false }) => {
  return (
    <section className="ui-card" aria-labelledby="information-card-title" data-testid="overview-information">
      <div className="pa3 bb b--moon-gray">
        <h2 id="information-card-title" className="f5 basier-med lh-copy ma0">
          Information
        </h2>
      </div>

      <div className={`${styles.Content} f6 lh-copy black`}>
        <div className={styles.Columns}>
          <dl className={styles.FieldList}>
            <InformationField label="Author" value={information.author} loading={loading} skeletonWidth="120px" />
            <InformationField label="Owner" value={information.owner} loading={loading} skeletonWidth="120px" />
            <InformationField
              label="Date created"
              value={<DateTimeRenderer value={information.createdAt} defaultResponse="—" />}
              loading={loading}
              skeletonWidth="150px"
            />
            <InformationField label="Type" value={information.type} loading={loading} skeletonWidth="80px" />
          </dl>

          <dl className={`${styles.FieldList} ${styles.RightColumn}`}>
            <InformationField
              label="Planned animals"
              value={information.plannedAnimals}
              loading={loading}
              skeletonWidth="40px"
            />
            <InformationField
              label="Living animals"
              value={information.livingAnimals}
              loading={loading}
              skeletonWidth="40px"
            />
            <InformationField
              label="Deceased animals"
              value={information.deceasedAnimals}
              loading={loading}
              skeletonWidth="40px"
            />
            <InformationField label="Groups" value={information.groups} loading={loading} skeletonWidth="40px" />
          </dl>
        </div>

        <dl className={`${styles.FieldList} ${styles.Description}`}>
          <InformationField
            label="Description"
            value={information.description}
            loading={loading}
            skeletonWidth="80%"
            skeletonLines={2}
          />
        </dl>
      </div>
    </section>
  );
};

export default InformationCard;
